import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CareerJobCard } from '../services/careerCatalogueApi';
import { jobPageApi, type JobPageContentBundle } from '../services/jobPageApi';
import { useApplication } from '../hooks/useApplication';
import { companyPath } from '../utils/company';
import { jobDisplayTitle } from '../utils/jobPagePresentation';
import ApplicationModal from './ApplicationModal';

function SelectedRole({ job, tabId, panelId }: { job: CareerJobCard; tabId: string; panelId: string }) {
  const [content, setContent] = useState<JobPageContentBundle | null>(null);
  const [error, setError] = useState(false);
  const { isApplicationModalOpen, openApplicationModal, closeApplicationModal } = useApplication();
  useEffect(() => {
    const controller = new AbortController();
    jobPageApi.getJobPageContent(job.slug, controller.signal).then(value => {
      if (!controller.signal.aborted) setContent(value);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => { controller.abort(); closeApplicationModal(); };
  }, [job.slug, closeApplicationModal]);
  const info = content?.jobInfo;
  return <div id={panelId} role="tabpanel" aria-labelledby={tabId} tabIndex={0} className="career-role-panel">
    <h3>{jobDisplayTitle(job.title)}</h3><p>{job.metaDescription}</p>
    <p className="career-role-location">{job.location}{job.salary ? ` · ${job.salary}` : ''}</p>
    {error ? <p role="alert">Die Details konnten nicht geladen werden. Öffne die Stellenseite, um es erneut zu versuchen.</p>
      : !content ? <p role="status">Stellendetails werden geladen …</p>
        : <div className="career-role-columns">
          <div><h4>Deine Aufgaben</h4><ul>{content.jobResponsibilities.tasks.map(task => <li key={task.id}><strong>{task.title}</strong><p>{task.description}</p></li>)}</ul></div>
          <div><h4>Dein Profil</h4><ul>{content.jobRequirements.requirements.map(item => <li key={item.id}><strong>{item.title}</strong><p>{item.description}</p></li>)}</ul></div>
        </div>}
    {job.skills.length > 0 && <ul className="career-card-skills" aria-label="Technologien">{job.skills.map(skill => <li key={skill}>{skill}</li>)}</ul>}
    <div className="career-hero-actions">
      {info && job.canApply && info.canApply !== false && <button type="button" className="career-button" onClick={() => openApplicationModal({ jobTitle: jobDisplayTitle(info.title), jobId: info.id, jobSlug: info.slug, ...info.applicationBinding })}>Für diese Rolle bewerben <span aria-hidden="true">↗</span></button>}
      <Link className="career-text-link" to={companyPath(`job/${encodeURIComponent(job.slug)}`)}>Alle Stellendetails <span aria-hidden="true">↗</span></Link>
    </div>
    <ApplicationModal isOpen={isApplicationModalOpen} onClose={closeApplicationModal} />
  </div>;
}

export default function CareerRoleComparison({ jobs }: { jobs: CareerJobCard[] }) {
  const [active, setActive] = useState(0);
  const id = useId();
  if (jobs.length < 2) return null;
  const tabId = (index: number) => `${id}-tab-${index}`;
  return <div className="career-role-comparison">
    <h3>Rollen vergleichen.</h3><p>Wähle eine Rolle und entdecke ihre Aufgaben und Anforderungen.</p>
    <div className="career-role-tabs" role="tablist" aria-label="Rolle auswählen">{jobs.map((job, index) => <button key={job.slug} id={tabId(index)} type="button" role="tab" aria-selected={active === index} aria-controls={`${id}-panel`} tabIndex={active === index ? 0 : -1}
      onClick={() => setActive(index)} onKeyDown={event => {
        const next = ({ ArrowRight: (index + 1) % jobs.length, ArrowLeft: (index + jobs.length - 1) % jobs.length, Home: 0, End: jobs.length - 1 } as Record<string, number>)[event.key];
        if (next === undefined) return;
        event.preventDefault(); setActive(next); document.getElementById(tabId(next))?.focus();
      }}>{jobDisplayTitle(job.title)}{!job.canApply && <small>Aktuell geschlossen</small>}</button>)}</div>
    <SelectedRole key={jobs[active].slug} job={jobs[active]} tabId={tabId(active)} panelId={`${id}-panel`} />
  </div>;
}
